import React, { useState } from "react";
import DataTable from "react-data-table-component";
import Select from "react-select";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import { toast } from "react-toastify";
import GeneralLedgerService from "../../../Services/generalLedger/GeneralLedgerService";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { can, PERMISSIONS } from "./glUtils";

const invalidateAll = (queryClient) =>
  ["glCompanies", "glMeta", "glEntries", "glStatements", "glBankProfiles"].forEach((key) =>
    queryClient.invalidateQueries({ queryKey: [key] })
  );

function CompanyModal({ company, onClose }) {
  const queryClient = useQueryClient();
  const isEdit = Boolean(company);
  const [form, setForm] = useState({
    name: company?.name || "",
    legal_name: company?.legal_name || "",
    tax_id: company?.tax_id || "",
    country: company?.country || "",
    base_currency: company?.base_currency || "",
    is_active: company?.is_active ?? true,
  });
  const set = (key, transform = (v) => v) => (e) =>
    setForm({ ...form, [key]: e.target.type === "checkbox" ? e.target.checked : transform(e.target.value) });

  const mutation = useMutation({
    mutationFn: () => {
      const payload = {
        name: form.name.trim(),
        legal_name: form.legal_name.trim() || null,
        tax_id: form.tax_id.trim() || null,
        country: form.country.trim() || null,
        is_active: Boolean(form.is_active),
      };
      const currency = form.base_currency.trim().toUpperCase();
      if (currency && !/^[A-Z]{3}$/.test(currency)) {
        return Promise.reject(new Error("Currency must be a 3-letter code such as TND or EUR."));
      }
      // The API accepts a missing currency on edit but rejects an explicit null.
      if (currency || !isEdit) payload.base_currency = currency || null;
      return isEdit ? GeneralLedgerService.updateCompany(company.id, payload) : GeneralLedgerService.createCompany(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? "Company updated" : "Company created");
      invalidateAll(queryClient);
      onClose();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, "Could not save the company.")),
  });

  return (
    <Modal isOpen toggle={onClose}>
      <ModalHeader toggle={onClose}>{isEdit ? "Edit company" : "New company"}</ModalHeader>
      <ModalBody>
        <Row className="g-3">
          <Col md="12">
            <Label>Name</Label>
            <Input value={form.name} maxLength={191} onChange={set("name")} />
          </Col>
          <Col md="12">
            <Label>Legal name</Label>
            <Input value={form.legal_name} maxLength={191} onChange={set("legal_name")} />
          </Col>
          <Col md="6">
            <Label>Tax ID</Label>
            <Input value={form.tax_id} maxLength={64} onChange={set("tax_id")} />
          </Col>
          <Col md="3">
            <Label>Country</Label>
            <Input value={form.country} maxLength={2} placeholder="TN" onChange={set("country", (v) => v.toUpperCase())} />
          </Col>
          <Col md="3">
            <Label>Currency</Label>
            <Input value={form.base_currency} maxLength={3} placeholder="TND" onChange={set("base_currency", (v) => v.toUpperCase())} />
          </Col>
          {isEdit && (
            <Col md="12">
              <Label check className="d-flex gap-2 align-items-center">
                <Input type="checkbox" checked={Boolean(form.is_active)} onChange={set("is_active")} /> Active
              </Label>
            </Col>
          )}
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>Cancel</Button>
        <Button color="primary" disabled={form.name.trim().length < 2 || mutation.isPending} onClick={() => mutation.mutate()}>
          {mutation.isPending ? "Saving…" : "Save"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function CompanyUsersModal({ company, onClose }) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState((company.users || []).map((u) => ({ value: u.id, label: `${u.name} (${u.email})` })));

  const usersQuery = useQuery({
    queryKey: ["glAssignableUsers"],
    queryFn: () => GeneralLedgerService.getAssignableUsers().then((r) => r.data),
  });

  const mutation = useMutation({
    mutationFn: () => GeneralLedgerService.syncCompanyUsers(company.id, selected.map((s) => s.value)),
    onSuccess: () => {
      toast.success("Company access updated");
      invalidateAll(queryClient);
      onClose();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, "Could not update access.")),
  });

  const options = (usersQuery.data || []).map((u) => ({ value: u.id, label: `${u.name} (${u.email})` }));

  return (
    <Modal isOpen toggle={onClose}>
      <ModalHeader toggle={onClose}>Staff access: {company.name}</ModalHeader>
      <ModalBody>
        <p className="gl-muted">
          Staff only see statements, rows and profiles of companies they are assigned to. Admins always see everything.
        </p>
        {usersQuery.isError && (
          <div className="alert alert-danger py-2">
            {getApiErrorMessage(usersQuery.error, "Could not load the staff list.")}
          </div>
        )}
        <Select
          isMulti
          isLoading={usersQuery.isLoading}
          options={options}
          value={selected}
          onChange={(v) => setSelected(v || [])}
          classNamePrefix="gl-select"
          placeholder="Select staff users…"
        />
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>Cancel</Button>
        <Button color="primary" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
          {mutation.isPending ? "Saving…" : "Save access"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default function CompaniesTab({ meta, onOpenLedger }) {
  const [editing, setEditing] = useState(null);
  const [managingUsers, setManagingUsers] = useState(null);
  const canManage = can(meta, PERMISSIONS.MANAGE_PROFILES);

  const companiesQuery = useQuery({
    queryKey: ["glCompanies"],
    queryFn: () => GeneralLedgerService.getCompanies().then((r) => r.data),
  });

  const columns = [
    {
      name: "Company",
      grow: 2,
      cell: (c) => (
        <div>
          <div className="gl-cell-strong">{c.name}</div>
          {c.legal_name && <div className="gl-muted">{c.legal_name}</div>}
        </div>
      ),
    },
    { name: "Tax ID", selector: (c) => c.tax_id || "—" },
    { name: "Country", width: "100px", selector: (c) => c.country || "—" },
    { name: "Currency", width: "100px", selector: (c) => c.base_currency || "—" },
    { name: "Accounts", width: "100px", right: true, selector: (c) => c.bank_profiles_count ?? 0 },
    ...(meta?.is_admin
      ? [{ name: "Staff", grow: 1.5, cell: (c) => (c.users || []).map((u) => u.name).join(", ") || <span className="gl-muted">None</span> }]
      : []),
    {
      name: "Status",
      width: "100px",
      cell: (c) => (c.is_active ? <Badge color="success">Active</Badge> : <Badge color="secondary">Inactive</Badge>),
    },
    {
      name: "",
      width: "230px",
      right: true,
      cell: (c) => (
        <div className="d-flex gap-1">
          <Button size="xs" color="light" onClick={() => onOpenLedger({ company_id: String(c.id) })}>Ledger</Button>
          {canManage && <Button size="xs" color="light" onClick={() => setEditing(c)}>Edit</Button>}
          {meta?.is_admin && <Button size="xs" color="light" onClick={() => setManagingUsers(c)}>Access</Button>}
        </div>
      ),
    },
  ];

  return (
    <div>
      {canManage && (
        <div className="text-end mb-3">
          <Button color="primary" onClick={() => setEditing("new")}>New company</Button>
        </div>
      )}
      {companiesQuery.isError && (
        <div className="alert alert-danger d-flex align-items-center justify-content-between gap-2">
          <span>{getApiErrorMessage(companiesQuery.error, "Could not load the companies.")}</span>
          <Button size="sm" color="light" onClick={() => companiesQuery.refetch()}>Try again</Button>
        </div>
      )}
      <DataTable
        columns={columns}
        data={companiesQuery.data || []}
        keyField="id"
        progressPending={companiesQuery.isLoading}
        highlightOnHover
        responsive
        noDataComponent={<div className="p-4 gl-muted">No companies yet.</div>}
      />
      {editing && <CompanyModal company={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
      {managingUsers && <CompanyUsersModal company={managingUsers} onClose={() => setManagingUsers(null)} />}
    </div>
  );
}
